import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DeleteButton } from "../components/DeleteButton";
import { HistoryTree } from "../components/HistoryTree";
import { api, type HistorySeries, type Identifiers, type PatientDetail, type Program } from "../api";
import { DebugModeCard } from "../components/DebugMode";
import { HomeRequirementsCard } from "../components/HomeRequirementsCard";
import { PatientPrograms } from "../components/PatientPrograms";
import { useAuth } from "../auth";
import { EYE_LABEL, errorText, useLoad } from "../hooks";

const STATUS_TEXT: Record<string, string> = {
  insufficient: "baseline not set yet", baseline_only: "baseline set", stable: "stable",
  possible_worse: "possible worsening", possible_better: "possible improvement",
  confirmed_worse: "confirmed worsening", confirmed_better: "confirmed improvement",
};

export function PatientPage() {
  const id = Number(useParams().id);
  const { user } = useAuth();
  const nav = useNavigate();
  const patient = useLoad(() => api.patient(id), [id]);
  const history = useLoad(() => api.history(id), [id]);
  const sessions = useLoad(() => api.patientSessions(id), [id]);
  const programs = useLoad(() => api.programs(), []);
  const [reveal, setReveal] = useState(false);
  const [editing, setEditing] = useState(false);
  const p = patient.data;
  const canRun = user?.role === "admin" || user?.role === "operator" || user?.role === "patient";
  const canEdit = user?.role === "admin" || user?.role === "operator";
  const canSetProgram = canEdit || user?.role === "researcher";
  const program = programs.data?.find((x) => x.id === p?.program_id) ?? programs.data?.find((x) => x.is_default) ?? null;

  if (patient.error) return <div className="error">{patient.error}</div>;
  if (!p) return <div>Loading…</div>;
  return (
    <>
      <h1>Patient {p.study_id}</h1>
      <div className="card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div>
            <div><span className="muted">Diagnosis:</span> {p.diagnosis || "—"}</div>
            {user?.role !== "patient" && (
              <div><span className="muted">Active program:</span> {program?.name ?? "—"}{p.program_id === null && program ? " (default)" : ""}
                {p.assigned_monitor_serial && <span className="muted small"> · monitor serial {p.assigned_monitor_serial}</span>}</div>
            )}
            {!p.identifiers ? (
              <div className="small muted" style={{ marginTop: "0.5rem" }}>No identifying details stored (study ID only).</div>
            ) : reveal ? (
              <div style={{ marginTop: "0.5rem" }}>
                <div><span className="muted">Name:</span> {p.identifiers.full_name ?? "—"}</div>
                <div><span className="muted">Date of birth:</span> {p.identifiers.date_of_birth ?? "—"}</div>
                <div><span className="muted">MRN:</span> {p.identifiers.mrn ?? "—"}</div>
                <div><span className="muted">Contact:</span> {[p.identifiers.email, p.identifiers.phone].filter(Boolean).join(", ") || "—"}</div>
                {p.identifiers.info && <div style={{ whiteSpace: "pre-wrap" }}><span className="muted">Patient information:</span> {p.identifiers.info}</div>}
              </div>
            ) : (
              <button className="link small" onClick={() => setReveal(true)}>Show identifying details</button>
            )}
          </div>
          <div className="row">
            {canRun && <Link to={`/patients/${id}/new-session`}><button>Start a session</button></Link>}
            {canEdit && <Link to={`/patients/${id}/calibrate`}><button className="secondary">Calibrate monitor</button></Link>}
            {canSetProgram && !editing && <button className="secondary" onClick={() => { setReveal(true); setEditing(true); }}>Edit details</button>}
          </div>
        </div>
        {editing && (
          <EditPatient p={p} programs={programs.data ?? []} programOnly={!canEdit}
            onDone={() => { setEditing(false); patient.reload(); }} />
        )}
      </div>
      {user?.role !== "patient" && (p.debug || user?.role === "admin") && (
        <DebugModeCard p={p} isAdmin={user?.role === "admin"} onChange={patient.reload} />
      )}

      {user?.role !== "patient" && programs.data && (
        <PatientPrograms patientId={id} programs={programs.data} canEdit={canSetProgram} />
      )}

      {user?.role !== "patient" && <HomeRequirementsCard key={`${id}-${p.program_id}`} patientId={id} canEdit={canEdit} />}

      {user?.role !== "patient" && <MonitorBrightness patientId={id} canCalibrate={canEdit} />}

      {user?.role === "admin" && (
        <div className="card">
          <DeleteButton what={`patient ${p.study_id}`} typeToConfirm={p.study_id}
            warning="Every session and result, their home station and their login go with them."
            onDelete={() => api.deletePatient(p.id, p.study_id)} onDone={() => nav("/patients")} />
          <span className="small muted"> Permanent: for demonstration entries, or erasing a person on request.</span>
        </div>
      )}

      <h2>History</h2>
      <div className="card">
        {sessions.error && <div className="error">{sessions.error}</div>}
        {sessions.data ? (
          <HistoryTree key={id} sessions={sessions.data} canRun={user?.role !== "researcher"} isAdmin={user?.role === "admin"}
            onChanged={() => { sessions.reload(); history.reload(); }} />
        ) : !sessions.error && <p className="muted">Loading…</p>}
      </div>

      <h2>Change over time</h2>
      <div className="card">
        {history.data?.series.length ? history.data.series.map((s) => <Series key={`${s.eye}-${s.kind}-${s.condition ?? ""}`} s={s} />)
          : <p className="muted">No verified results yet.</p>}
      </div>
    </>
  );
}

/** The patient's monitor(s) and their lux-meter baselines (staff). */
function MonitorBrightness({ patientId, canCalibrate }: { patientId: number; canCalibrate: boolean }) {
  const list = useLoad(() => api.patientBaselines(patientId), [patientId]);
  const current = (list.data ?? []).filter((b) => b.active);
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Monitor brightness (lux meter)</h2>
      {list.error && <div className="error">{list.error}</div>}
      {current.length ? (
        <ul style={{ margin: 0 }}>
          {current.map((b) => (
            <li key={b.id}><strong>{b.white_lux} lux</strong> on {b.monitor?.name ?? b.screen_key}
              {b.monitor?.serial ? ` (serial ${b.monitor.serial})` : ""}
              <span className="small muted"> · {b.set_at ? new Date(b.set_at).toLocaleDateString() : ""},
                {b.source === "staff_calibrate" ? ` Calibrate by ${b.set_by ?? "staff"}` : " first home session"}</span></li>
          ))}
        </ul>
      ) : <p className="small muted" style={{ margin: 0 }}>No baseline yet. Set one with Calibrate monitor at kit set-up, or the
        patient’s first home session will save one.</p>}
      {canCalibrate && <p className="small" style={{ marginBottom: 0 }}><Link to={`/patients/${patientId}/calibrate`}>Calibrate monitor</Link>
        {" "}— on the computer and monitor the patient will use.</p>}
    </div>
  );
}

function EditPatient({ p, programs, programOnly, onDone }: {
  p: PatientDetail; programs: Program[]; programOnly: boolean; onDone: () => void;
}) {
  const id0: Identifiers = p.identifiers ?? {};
  const [f, setF] = useState({
    full_name: id0.full_name ?? "", date_of_birth: id0.date_of_birth ?? "", mrn: id0.mrn ?? "",
    email: id0.email ?? "", phone: id0.phone ?? "", info: id0.info ?? "", diagnosis: p.diagnosis,
    notes: p.notes, program_id: p.program_id === null ? "" : String(p.program_id), serial: p.assigned_monitor_serial ?? "",
  });
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setError(null);
    const program_id = f.program_id ? Number(f.program_id) : null;
    try {
      if (programOnly) await api.updatePatient(p.id, { program_id });
      else {
        const t = (v: string) => v.trim() || null;
        await api.updatePatient(p.id, {
          identifiers: { full_name: t(f.full_name), date_of_birth: f.date_of_birth || null, mrn: t(f.mrn),
            email: t(f.email), phone: t(f.phone), info: t(f.info) },
          diagnosis: f.diagnosis, notes: f.notes, program_id, assigned_monitor_serial: t(f.serial),
        });
      }
      onDone();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <div style={{ marginTop: "1rem", borderTop: "1px solid #dde3ea", paddingTop: "0.8rem" }}>
      <div className="row">
        <label>Program
          <select aria-label="Program" value={f.program_id} onChange={set("program_id")}>
            <option value="">default program</option>
            {programs.filter((x) => x.active).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
        {!programOnly && (
          <>
            <label>Monitor serial<input value={f.serial} onChange={set("serial")} placeholder="monitor sent home, from its EDID" /></label>
            <label>Full name<input value={f.full_name} onChange={set("full_name")} /></label>
            <label>Date of birth<input type="date" value={f.date_of_birth} onChange={set("date_of_birth")} /></label>
            <label>MRN<input value={f.mrn} onChange={set("mrn")} /></label>
            <label>Email<input type="email" value={f.email} onChange={set("email")} /></label>
            <label>Phone<input value={f.phone} onChange={set("phone")} /></label>
            <label>Diagnosis<input value={f.diagnosis} onChange={set("diagnosis")} /></label>
            <label style={{ flexBasis: "100%" }}>Patient information (encrypted)
              <textarea rows={3} value={f.info} onChange={set("info")} />
            </label>
            <label style={{ flexBasis: "100%" }}>Study notes (no identifying details)
              <textarea rows={2} value={f.notes} onChange={set("notes")} />
            </label>
          </>
        )}
      </div>
      {error && <div className="error">{error}</div>}
      <div className="row">
        <button onClick={() => void save()}>Save</button>
        <button className="secondary" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

const ALERT_TEXT: Record<string, string> = { repeat: "changed once (repeat asked)", contact: "changed twice: patient told to contact the team" };

function Series({ s }: { s: HistorySeries }) {
  const unit = s.kind === "acuity" ? "logMAR" : s.measure === "log_modulation" ? "log modulation" : "log CS";
  const a = s.assessment;
  const cls = a.status.endsWith("worse") ? "bad" : a.status.endsWith("better") ? "good" : "muted";
  return (
    <div style={{ marginBottom: "1rem" }}>
      <strong>{EYE_LABEL[s.eye] ?? s.eye} — {s.kind === "acuity" ? "acuity" : "contrast sensitivity"}</strong>{" "}
      {s.condition && s.condition !== "standard" && <span className="muted small">({s.condition}) </span>}
      <span className={cls}>{STATUS_TEXT[a.status] ?? a.status}</span>
      {a.delta !== null && <span className="muted small"> (change {a.delta >= 0 ? "+" : ""}{a.delta.toFixed(2)} {unit} vs baseline from {a.baseline_from ?? "?"}; CoR {a.cor})</span>}
      {a.alert && a.alert !== "none" && <span className={a.alert === "contact" ? "bad" : "warn"}> · {ALERT_TEXT[a.alert]}</span>}
      <Sparkline s={s} />
    </div>
  );
}

function Sparkline({ s }: { s: HistorySeries }) {
  const pts = s.points;
  if (pts.length < 2) return <div className="muted small">{pts.map((p) => p.value.toFixed(2)).join(", ")}</div>;
  const w = 520, h = 90, pad = 24;
  const xs = pts.map((p) => new Date(p.when).getTime());
  const ys = pts.map((p) => p.value);
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const lo = Math.min(...ys) - 0.1, hi = Math.max(...ys) + 0.1;
  const X = (t: number) => pad + ((t - x0) / Math.max(1, x1 - x0)) * (w - 2 * pad);
  // acuity: lower logMAR is better, so plot it upward
  const Y = (v: number) => s.kind === "acuity" ? pad / 2 + ((v - lo) / (hi - lo)) * (h - pad) : h - pad / 2 - ((v - lo) / (hi - lo)) * (h - pad);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${X(xs[i]!).toFixed(1)} ${Y(p.value).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} role="img" aria-label="history">
      <path d={d} fill="none" stroke="#8a96a3" />
      {pts.map((p, i) => (
        <circle key={i} cx={X(xs[i]!)} cy={Y(p.value)} r={4} fill={p.mode === "clinic" ? "#2a6fdb" : "#fff"} stroke="#2a6fdb">
          <title>{`${new Date(p.when).toLocaleDateString()}: ${p.value.toFixed(2)} (${p.mode})`}</title>
        </circle>
      ))}
    </svg>
  );
}
